import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { serialApi, SerialItem } from '@/api/serial.api';

interface SerialSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  warehouseId?: string;
  requiredQty: number;
  selectedSerialIds: string[];
  reservedSerialIds?: Record<string, { rowNum: number }>;
  onConfirm: (selectedSerials: SerialItem[]) => void;
}

export const SerialSelectModal: React.FC<SerialSelectModalProps> = ({
  isOpen,
  onClose,
  itemId,
  itemName,
  warehouseId,
  requiredQty,
  selectedSerialIds = [],
  reservedSerialIds = {},
  onConfirm,
}) => {
  const [availableSerials, setAvailableSerials] = useState<SerialItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [barcodeScan, setBarcodeScan] = useState('');
  const [scanMessage, setScanMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && itemId) {
      setSelectedIds([...selectedSerialIds]);
      setScanMessage(null);
      setBarcodeScan('');
      loadAvailableSerials();
    }
  }, [isOpen, itemId, warehouseId]);

  const loadAvailableSerials = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await serialApi.getAvailable(itemId, warehouseId);
      if (res.success) {
        setAvailableSerials(res.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load available serials');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentCount = selectedIds.length;
  const isTargetMet = currentCount === requiredQty;

  const toggleSelect = (id: string) => {
    if (reservedSerialIds[id]) {
      setScanMessage({
        text: `Serial is already reserved in Row #${reservedSerialIds[id].rowNum}.`,
        type: 'error',
      });
      return;
    }

    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      if (selectedIds.length >= requiredQty) {
        setScanMessage({ text: `Cannot select more than required quantity (${requiredQty}).`, type: 'error' });
        return;
      }
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    const query = barcodeScan.trim().toLowerCase();
    if (!query) return;

    const matched = availableSerials.find(
      s => s.serial_number.toLowerCase() === query ||
           (s.vendor_serial_number && s.vendor_serial_number.toLowerCase() === query) ||
           (s.imei && s.imei.toLowerCase() === query)
    );

    if (!matched) {
      setScanMessage({ text: `Serial "${barcodeScan}" not found in available stock!`, type: 'error' });
      return;
    }

    if (reservedSerialIds[matched.id]) {
      setScanMessage({
        text: `Serial "${matched.serial_number}" is already reserved in Row #${reservedSerialIds[matched.id].rowNum}!`,
        type: 'error',
      });
      return;
    }

    if (selectedIds.includes(matched.id)) {
      setScanMessage({ text: `Serial "${matched.serial_number}" is already selected.`, type: 'error' });
      return;
    }

    if (selectedIds.length >= requiredQty) {
      setScanMessage({ text: `Quantity limit reached (${requiredQty} items).`, type: 'error' });
      return;
    }

    setSelectedIds([...selectedIds, matched.id]);
    setScanMessage({ text: `Scanned & Selected: ${matched.serial_number}`, type: 'success' });
    setBarcodeScan('');
  };

  const handleAutoPickFIFO = () => {
    const unreserved = availableSerials.filter(s => !reservedSerialIds[s.id]);
    const needed = requiredQty;
    const oldest = unreserved.slice(0, needed).map(s => s.id);
    setSelectedIds(oldest);
    setScanMessage({
      text: `Auto-picked ${oldest.length} serials based on FIFO (${availableSerials.length - unreserved.length} reserved serials skipped).`,
      type: 'success',
    });
  };

  const handleClearAll = () => {
    setSelectedIds([]);
    setScanMessage(null);
  };

  const handleConfirm = () => {
    if (selectedIds.length !== requiredQty) {
      alert(`Please select exactly ${requiredQty} serial number(s). Currently selected: ${selectedIds.length}.`);
      return;
    }
    const chosenObjects = availableSerials.filter(s => selectedIds.includes(s.id));
    onConfirm(chosenObjects);
    onClose();
  };

  const reservedCount = Object.keys(reservedSerialIds).length;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-blue-600 text-white rounded-lg text-sm">
                <i className="ri-checkbox-multiple-line"></i>
              </span>
              <h2 className="text-lg font-bold text-gray-900">Select Serials for Invoice</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Item: <span className="font-semibold text-gray-800">{itemName}</span> • Need: <span className="font-bold text-blue-600">{requiredQty} units</span>
              {reservedCount > 0 && (
                <span className="text-amber-700 font-medium ml-2">
                  • ({reservedCount} unit{reservedCount > 1 ? 's' : ''} reserved in other rows)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Scan & Quick Actions bar */}
        <div className="px-6 py-3 bg-gray-50/70 border-b border-gray-100 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 items-center justify-between">
            <form onSubmit={handleScan} className="flex gap-2 w-full sm:w-80">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-400">
                  <i className="ri-barcode-line"></i>
                </div>
                <input
                  type="text"
                  value={barcodeScan}
                  onChange={(e) => setBarcodeScan(e.target.value)}
                  placeholder="Scan barcode or type serial..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                type="submit"
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold"
              >
                Scan
              </button>
            </form>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <button
                type="button"
                onClick={handleAutoPickFIFO}
                className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-1 shadow-sm transition-all"
              >
                <i className="ri-magic-line"></i> Auto-Pick (FIFO)
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="px-3 py-1.5 text-xs font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {scanMessage && (
            <div
              className={`p-2 rounded-lg text-xs flex items-center gap-1.5 ${
                scanMessage.type === 'error'
                  ? 'bg-rose-50 text-rose-700 border border-rose-200'
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}
            >
              <i className={scanMessage.type === 'error' ? 'ri-error-warning-line' : 'ri-check-line'}></i>
              <span>{scanMessage.text}</span>
            </div>
          )}
        </div>

        {/* Serials Selection Table */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="py-12 text-center text-gray-500">
              <i className="ri-loader-4-line text-2xl animate-spin block mb-2 text-blue-600"></i>
              Loading available stock serials...
            </div>
          ) : error ? (
            <div className="p-4 bg-rose-50 text-rose-700 rounded-xl text-xs border border-rose-200">
              {error}
            </div>
          ) : availableSerials.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <i className="ri-inbox-2-line text-4xl block mb-2 opacity-50"></i>
              No available serials in stock for this item in the selected warehouse.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="py-2.5 px-3 w-10 text-center">Select</th>
                    <th className="py-2.5 px-3">Serial No</th>
                    <th className="py-2.5 px-3">Vendor SN / Tag</th>
                    <th className="py-2.5 px-3">Warehouse</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Condition</th>
                    <th className="py-2.5 px-3">Warranty Till</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {availableSerials.map((s) => {
                    const isReserved = Boolean(reservedSerialIds[s.id]);
                    const reservedInfo = reservedSerialIds[s.id];
                    const isSelected = selectedIds.includes(s.id);
                    return (
                      <tr
                        key={s.id}
                        onClick={() => !isReserved && toggleSelect(s.id)}
                        className={`transition-colors ${
                          isReserved
                            ? 'bg-amber-50/30 text-gray-400 cursor-not-allowed opacity-65'
                            : isSelected
                            ? 'bg-blue-50/60 font-medium cursor-pointer'
                            : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isReserved}
                            onChange={() => !isReserved && toggleSelect(s.id)}
                            className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                          />
                        </td>
                        <td className="py-2 px-3 font-mono font-medium text-gray-900">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{s.serial_number}</span>
                            {isReserved && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                🔒 Row #{reservedInfo?.rowNum}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{s.vendor_serial_number || '—'}</td>
                        <td className="py-2 px-3 text-gray-600">{s.warehouse_name || '—'}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            isReserved
                              ? 'bg-gray-100 text-gray-600 border border-gray-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {isReserved ? 'RESERVED' : s.status}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            s.condition === 'GOOD'
                              ? 'bg-blue-50 text-blue-700'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                            {s.condition}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-500 font-mono">
                          {s.warranty_expiry_date || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div className="text-xs">
            <span className="font-semibold text-gray-700">
              Selected: <span className={isTargetMet ? 'text-emerald-600 font-bold' : 'text-blue-600 font-bold'}>{currentCount}</span> / {requiredQty}
            </span>
            {currentCount < requiredQty && (
              <span className="text-amber-600 ml-2">
                (Need {requiredQty - currentCount} more)
              </span>
            )}
            {currentCount > requiredQty && (
              <span className="text-rose-600 ml-2">
                (Remove {currentCount - requiredQty})
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-xl text-xs font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!isTargetMet}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-md transition-all flex items-center gap-1.5"
            >
              <i className="ri-check-line"></i> Confirm Selection ({currentCount}/{requiredQty})
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
